import { useState } from 'react';
import { useEvaluationDetails } from '../../hooks/home/markView/useEvaluationDetails';
import { useMarkSubmission } from '../../hooks/home/markView/useMarkSubmission';

function ToggleSwitch({ checked, onChange, labelOn, labelOff, disabled, color = '#28a745' }) {
    return (
        <div
            className="d-flex align-items-center gap-2"
            style={{ userSelect: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }}
            onClick={() => !disabled && onChange(!checked)}
        >
            <div style={{
                width: 40, height: 22, borderRadius: 11,
                background: checked ? color : '#ced4da',
                position: 'relative',
                transition: 'background 0.22s',
                flexShrink: 0,
                opacity: disabled ? 0.5 : 1,
            }}>
                <div style={{
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff',
                    position: 'absolute', top: 3,
                    left: checked ? 21 : 3,
                    transition: 'left 0.22s',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                }} />
            </div>
            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: checked ? color : '#6c757d', minWidth: 52 }}>
                {checked ? labelOn : labelOff}
            </span>
        </div>
    );
}

function PrerequisitesPanel({ internshipStatus, attendanceStatus, onInternshipChange, onAttendanceChange, canEditInternship, canEditAttendance, onClose }) {
    return (
        <div style={{ background: '#fafafa', border: '1px solid #e4e4e4', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 12 }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#581f37' }}>
                    <i className="fa fa-sliders me-2" />Pre-requisites
                </span>
                {onClose && (
                    <button type="button" onClick={onClose}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#aaa', lineHeight: 1, fontSize: '0.8rem' }}
                        title="Close">
                        <i className="fa fa-times" />
                    </button>
                )}
            </div>

            <div className="row g-2">
                <div className="col-md-6">
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
                        style={{ background: '#fff', border: '1px solid #ececec' }}>
                        <span style={{ fontSize: '0.83rem', color: '#444', fontWeight: 500 }}>Internship</span>
                        <ToggleSwitch
                            checked={internshipStatus === 'complete'}
                            onChange={(val) => onInternshipChange(val ? 'complete' : 'pending')}
                            labelOn="Complete" labelOff="Pending"
                            disabled={!canEditInternship} color="#28a745"
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
                        style={{ background: '#fff', border: '1px solid #ececec' }}>
                        <span style={{ fontSize: '0.83rem', color: '#444', fontWeight: 500 }}>Attendance</span>
                        <ToggleSwitch
                            checked={attendanceStatus === 'present'}
                            onChange={(val) => onAttendanceChange(val ? 'present' : 'absent')}
                            labelOn="Present" labelOff="Present"
                            disabled={!canEditAttendance} color="#17a2b8"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function MarkView({ studentId, onBack, onSubmitSuccess }) {
    const [refreshKey, setRefreshKey] = useState(0);
    const { evaluationData, isLoading } = useEvaluationDetails(studentId, onBack, refreshKey);
    const [entryStatus, setEntryStatus] = useState(null);

    const [internshipStatus, setInternshipStatus] = useState(null);
    const [attendanceStatus, setAttendanceStatus] = useState(undefined);
    const [apiInternshipStatus, setApiInternshipStatus] = useState(null);
    const [apiAttendanceStatus, setApiAttendanceStatus] = useState(undefined);
    const [statusInitialized, setStatusInitialized] = useState(false);
    const [prereqOpen, setPrereqOpen] = useState(true);
    const [savedAsAbsent, setSavedAsAbsent] = useState(false);

    if (evaluationData && !statusInitialized) {
        const apiIS = evaluationData.internship_status ?? 'pending';
        const apiAS = evaluationData.attendance_status ?? null;

        const effectiveIS = apiAS === null ? 'complete' : apiIS;
        const effectiveAS = apiAS === null ? 'present' : apiAS;

        setInternshipStatus(effectiveIS);
        setAttendanceStatus(effectiveAS);
        setApiInternshipStatus(apiIS); 
        setApiAttendanceStatus(apiAS);
        setPrereqOpen(true);
        setStatusInitialized(true);
    }

    const handleSaveSuccess = () => {
        setApiInternshipStatus(internshipStatus);
        setApiAttendanceStatus(attendanceStatus);
        setPrereqOpen(attendanceStatus === 'absent');
        if (attendanceStatus === 'absent') setSavedAsAbsent(true);
        onSubmitSuccess?.();
    };

    const handleRefresh = () => {
        setEntryStatus(null);
        setStatusInitialized(false);
        setPrereqOpen(true);
        setRefreshKey(k => k + 1);
    };

    const normalizedStatus = entryStatus !== null
        ? entryStatus
        : (evaluationData?.entry_status ?? 'pending');

    const { cca, ese, isSubmitting, handleSubmit, handleCancel, handleCcaChange, handleEseChange } =
        useMarkSubmission(
            studentId, evaluationData, onBack, handleSaveSuccess, handleRefresh,
            (s) => setEntryStatus(s),
            internshipStatus, attendanceStatus,
        );

    const internshipComplete = internshipStatus === 'complete';
    const attendanceMarked = attendanceStatus === 'present' || attendanceStatus === 'absent';
    const isAbsentFlow = attendanceStatus === 'absent';

    const statusesReady = isAbsentFlow ? true : (internshipComplete && attendanceMarked);

    const handleInternshipChange = (val) => {
        setInternshipStatus(val);
    };

    const handleAttendanceChange = (val) => {
        setAttendanceStatus(val);
        if (val === 'absent' || val === null) {
            setPrereqOpen(true);
        }
    };

    if (isLoading) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <div className="spinner-border" role="status" style={{ color: '#581f37' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading student details...</p>
                </div>
            </div>
        );
    }

    if (!evaluationData) {
        return (
            <div className="card">
                <div className="card-body text-center py-5">
                    <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3" />
                    <p>No student data available</p>
                    <button className="btn btn-primary" onClick={onBack}>Back</button>
                </div>
            </div>
        );
    }

    const isEntryLocked = normalizedStatus === 'submitted';
    const isNa = normalizedStatus === 'na';
    const isFormDisabled = isEntryLocked || isNa;
    const isRevertedStatus = normalizedStatus === 'reverted';
    const hasRevertFlag = evaluationData?.revert === '1';
    const showOnlyRevertBadge = isRevertedStatus;
    const showExtraRevertBadge = hasRevertFlag && !isRevertedStatus;

    const canEditInternship = !isEntryLocked;
    const canEditAttendance = !isEntryLocked;

    const statusConfig = {
        pending: { label: 'Pending', badgeClass: 'bg-warning text-dark', icon: 'fa-clock-o', alertClass: 'alert-info', alertText: 'This mark entry is pending submission.' },
        draft: { label: 'Draft', badgeClass: 'bg-info text-dark', icon: 'fa-file-text-o', alertClass: 'alert-info', alertText: 'This mark entry has been saved as a draft.' },
        submitted: { label: 'Submitted', badgeClass: 'bg-danger', icon: 'fa-lock', alertClass: 'alert-warning', alertText: 'This mark entry has been submitted and locked. It cannot be modified.' },
        na: { label: 'N/A', badgeClass: 'bg-secondary', icon: 'fa-ban', alertClass: 'alert-secondary', alertText: 'This entry is not available.' },
        reverted: { label: 'Reverted', badgeClass: '', badgeStyle: { backgroundColor: '#e65c00', color: '#fff' }, icon: 'fa-solid fa-reply', alertClass: 'alert-warning', alertText: 'This entry has been reverted. Please review and resubmit.' },
    };

    const revertBadgeStyle = { backgroundColor: '#e65c00', color: '#fff' };
    const currentStatus = statusConfig[normalizedStatus] ?? null;

    const showPrereqClose = attendanceStatus === 'present' && attendanceMarked;

    const isSaveDisabled =
        isSubmitting ||
        isFormDisabled ||
        attendanceStatus === null ||   
        (attendanceStatus === 'present' && (!internshipComplete || (!cca && !ese)));

    return (
        <div className="card">
            <div className="col-md-12">

                <div className="card-header">
                    <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h5 style={{ color: '#581f37', fontWeight: 700, margin: 0 }}>
                            <i className="fa fa-edit me-2" />Mark Entry
                        </h5>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {showOnlyRevertBadge ? (
                                <span className="badge" style={revertBadgeStyle}>
                                    <i className="fa-solid fa-reply me-1" />Reverted
                                </span>
                            ) : (
                                <>
                                    {currentStatus && (
                                        <span className={`badge ${currentStatus.badgeClass}`} style={currentStatus.badgeStyle || {}}>
                                            <i className={`fa ${currentStatus.icon} me-1`} />{currentStatus.label}
                                        </span>
                                    )}
                                    {showExtraRevertBadge && (
                                        <span className="badge" style={revertBadgeStyle}>
                                            <i className="fa-solid fa-reply me-1" />Reverted
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <div className="card-body">

                    {(isRevertedStatus || hasRevertFlag) && (
                        <div className="alert alert-warning mb-3 d-flex align-items-start gap-2" role="alert">
                            <i className="fa-solid fa-reply mt-1" style={{ color: '#e65c00', flexShrink: 0 }} />
                            <div><strong>Reverted:</strong> {statusConfig.reverted.alertText}</div>
                        </div>
                    )}

                    {isFormDisabled && !isRevertedStatus && !hasRevertFlag && currentStatus && (
                        <div className={`alert ${currentStatus.alertClass} mb-3`} role="alert">
                            <i className={`fa ${currentStatus.icon} me-2`} />{currentStatus.alertText}
                        </div>
                    )}

                    <div className="row mb-3">
                        <div className="col-md-12">
                            <div className="card bg-light border-0">
                                <div className="card-body">
                                    <div className="row text-dark">
                                        <div className="col-md-6 mb-2"><strong>Student:</strong> {evaluationData.student}</div>
                                        <div className="col-md-6 mb-2"><strong>Exam:</strong> {evaluationData.examtitle}</div>
                                        <div className="col-md-6 mb-2"><strong>Course:</strong> {evaluationData.course}</div>
                                        <div className="col-md-6 mb-2"><strong>Discipline:</strong> {evaluationData.discipline}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {attendanceStatus !== undefined && (
                        <>
                            {prereqOpen && (
                                <PrerequisitesPanel
                                    internshipStatus={internshipStatus}
                                    attendanceStatus={attendanceStatus}
                                    onInternshipChange={handleInternshipChange}
                                    onAttendanceChange={handleAttendanceChange}
                                    canEditInternship={canEditInternship}
                                    canEditAttendance={canEditAttendance && internshipComplete}
                                    onClose={showPrereqClose ? () => setPrereqOpen(false) : null}
                                />
                            )}

                            {!prereqOpen && attendanceStatus === 'present' && (
                                <div className="mb-3">
                                    <span
                                        onClick={() => setPrereqOpen(true)}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 5,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            color: '#581f37',
                                            background: '#f9f0f3',
                                            border: '1px solid #e8cdd5',
                                            borderRadius: 20,
                                            padding: '3px 10px',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <i className="fa fa-sliders" style={{ fontSize: '0.7rem' }} />
                                        Pre-requisites
                                        <i className="fa fa-chevron-down" style={{ fontSize: '0.6rem', opacity: 0.6 }} />
                                    </span>
                                </div>
                            )}
                        </>
                    )}


                    <form onSubmit={(e) => { e.preventDefault(); if (!isFormDisabled) handleSubmit(); }}>

                        {attendanceStatus !== undefined && !isAbsentFlow && statusesReady && (
                            <div className="card bg-light border-0 text-dark">
                                <div className="card-body">
                                    <div className="row g-3 mb-3">

                                        <div className="col-md-6">
                                            <label className="form-label fw-bold" htmlFor="ccaEntry">CCA Mark</label>
                                            <div className="input-group">
                                                <input
                                                    type="number" id="ccaEntry" name="ccaEntry"
                                                    className="form-control custom-input"
                                                    placeholder="Enter CCA mark"
                                                    value={cca !== '' && cca !== null && cca !== undefined ? Math.floor(Number(cca)) : ''}
                                                    step="1" min="0" max={evaluationData.cca_max || 'N/A'}
                                                    autoComplete="off"
                                                    onChange={handleCcaChange}
                                                    onWheel={(e) => e.target.blur()}
                                                    onKeyDown={(e) => ['e', 'E', '.', ',', '+', '-'].includes(e.key) && e.preventDefault()}
                                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                                    disabled={isSubmitting || isFormDisabled}
                                                    readOnly={isFormDisabled}
                                                    title={`${isFormDisabled ? 'Entry is not editable' : 'Enter CCA mark'}, 0 – ${evaluationData.cca_max || 100}`}
                                                />
                                                <div className="form-floating">
                                                    <input type="text" className="form-control custom-input bg-light text-dark"
                                                        value={evaluationData.cca_max || 'N/A'} disabled id="ccaMaxMarkInput"
                                                        style={{ fontSize: '0.9rem' }} readOnly />
                                                    <label htmlFor="ccaMaxMarkInput">Max Mark</label>
                                                </div>
                                            </div>
                                            <small className="text-muted">
                                                {(() => {
                                                    const maxCO = 11;
                                                    const needed = Number(evaluationData.outcome_number);
                                                    const values = [];
                                                    for (let i = 1; i <= maxCO; i++) {
                                                        const val = evaluationData[`interal_co${i}`];
                                                        if (val !== null && val !== undefined && val !== '') values.push({ num: i, val });
                                                    }
                                                    const finalValues = !needed || needed === 0 ? values : values.slice(0, needed);
                                                    return finalValues.map(({ num, val }) => (
                                                        <span key={num} className="me-2"><strong>CO{num}:</strong> {val}</span>
                                                    ));
                                                })()}
                                            </small>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-bold" htmlFor="eseEntry">ESE Mark</label>
                                            <div className="input-group">
                                                <input
                                                    type="number" id="eseEntry" name="eseEntry"
                                                    className="form-control custom-input"
                                                    placeholder="Enter ESE mark"
                                                    value={ese !== '' && ese !== null && ese !== undefined ? Math.floor(Number(ese)) : ''}
                                                    step="1" min="0" max={evaluationData.ese_max || 'N/A'}
                                                    autoComplete="off"
                                                    onChange={handleEseChange}
                                                    onWheel={(e) => e.target.blur()}
                                                    onKeyDown={(e) => ['e', 'E', '.', ',', '+', '-'].includes(e.key) && e.preventDefault()}
                                                    onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                                                    disabled={isSubmitting || isFormDisabled}
                                                    readOnly={isFormDisabled}
                                                    title={`${isFormDisabled ? 'Entry is not editable' : 'Enter ESE mark'}, 0 – ${evaluationData.ese_max || 100}`}
                                                />
                                                <div className="form-floating">
                                                    <input type="text" className="form-control custom-input bg-light text-dark"
                                                        value={evaluationData.ese_max || 'N/A'} disabled id="eseMaxMarkInput"
                                                        style={{ fontSize: '0.9rem' }} readOnly />
                                                    <label htmlFor="eseMaxMarkInput">Max Mark</label>
                                                </div>
                                            </div>
                                            <small className="text-muted">
                                                {(() => {
                                                    const maxCO = 11;
                                                    const needed = Number(evaluationData.outcome_number);
                                                    const values = [];
                                                    for (let i = 1; i <= maxCO; i++) {
                                                        const val = evaluationData[`external_co${i}`];
                                                        if (val) values.push({ num: i, val });
                                                    }
                                                    const finalValues = !needed || needed === 0 ? values : values.slice(0, needed);
                                                    return finalValues.map(({ num, val }) => (
                                                        <span key={num} className="me-2"><strong>CO{num}:</strong> {val}</span>
                                                    ));
                                                })()}
                                            </small>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}

                        {attendanceStatus === 'absent' && (
                            <div className="alert alert-warning d-flex align-items-center gap-2 mb-3" role="alert">
                                <i className="fa fa-user-times" style={{ fontSize: '1.1rem', color: '#856404' }} />
                                <div>
                                    <strong>Student is Absent.</strong> Mark entry is not required. Click <strong>Save</strong> to record the attendance.
                                </div>
                            </div>
                        )}

                        <div className="row mt-4">
                            <div className="col-md-12 d-flex justify-content-center gap-3">
                                <button type="button" className="btn btn-light px-4" onClick={handleCancel} disabled={isSubmitting}>
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary px-4"
                                    disabled={isSaveDisabled}
                                    title={isFormDisabled ? 'Entry is not editable' : 'Save'}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            {isFormDisabled && <i className="fa fa-lock me-2" />}
                                            Save
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>

                </div>
            </div>

            <style>{`
                .custom-input:focus { border-color: #581f37; box-shadow: 0 0 0 0.25rem rgba(88,31,55,0.25); }
                .input-group .form-floating { flex: 0 0 auto; width: auto; min-width: 120px; }
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button { opacity: 1; }
                input[readonly] { background-color: #e9ecef !important; cursor: not-allowed; }
            `}</style>
        </div>
    );
}

export default MarkView;